import { useState } from "react";
import {
  ClipboardList,
  ChevronDown,
  MessageSquarePlus,
  Clock,
  CheckCircle2,
  AlertCircle,
  Circle,
} from "lucide-react";
import { Badge, statusVariant } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card } from "../../../components/ui/Card";
import { Modal } from "../../../components/ui/Modal";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { EmptyState } from "../../../components/ui/EmptyState";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useMyTasks, useUpdateTaskStatus, useAddTaskUpdate } from "../../../hooks/useTask";
import type { Task, TaskStatus } from "../../../lib/taskApi";
import { formatDate } from "../../../utils/format";

// ── Priority helpers ──────────────────────────────────────────────────────────

const PRIORITY_COLOR: Record<string, string> = {
  HIGH: "text-[#dc2626] bg-[#fef2f2]",
  MEDIUM: "text-[#d97706] bg-[#fffbeb]",
  LOW: "text-[#64748b] bg-[#f1f5f9]",
};

const STATUS_ICON: Record<TaskStatus, React.ReactNode> = {
  TODO: <Circle className="h-4 w-4 text-[#94a3b8]" />,
  IN_PROGRESS: <Clock className="h-4 w-4 text-[#2447d9]" />,
  DONE: <CheckCircle2 className="h-4 w-4 text-[#16a34a]" />,
  BLOCKED: <AlertCircle className="h-4 w-4 text-[#dc2626]" />,
};

const NEXT_STATUSES: Record<TaskStatus, TaskStatus[]> = {
  TODO: ["IN_PROGRESS"],
  IN_PROGRESS: ["DONE", "BLOCKED"],
  BLOCKED: ["IN_PROGRESS"],
  DONE: [],
};

// ── Task Detail Modal ─────────────────────────────────────────────────────────

interface TaskDetailProps {
  task: Task;
  onClose: () => void;
  onRefresh: () => void;
}

function TaskDetailModal({ task, onClose, onRefresh }: TaskDetailProps) {
  const { toasts, toast, dismissToast } = useToast();
  const updateStatus = useUpdateTaskStatus();
  const addUpdate = useAddTaskUpdate();
  const [note, setNote] = useState("");

  const handleStatusChange = async (newStatus: TaskStatus) => {
    try {
      await updateStatus.mutateAsync({ taskId: task.taskId, status: newStatus });
      toast.success(`Status updated to ${newStatus.replace("_", " ")}.`);
      onRefresh();
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to update status. Please try again.");
    }
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    try {
      await addUpdate.mutateAsync({ taskId: task.taskId, note: note.trim() });
      toast.success("Progress note added.");
      setNote("");
      onRefresh();
    } catch {
      toast.error("Failed to add note. Please try again.");
    }
  };

  const nextStatuses = NEXT_STATUSES[task.status];

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-5">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusVariant(task.status)}>
            <span className="flex items-center gap-1">
              {STATUS_ICON[task.status]}
              {task.status.replace("_", " ")}
            </span>
          </Badge>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${PRIORITY_COLOR[task.priority]}`}>
            {task.priority}
          </span>
          {task.dueDate && (
            <span className="text-xs text-[#64748b]">Due {formatDate(task.dueDate)}</span>
          )}
        </div>

        {/* Description */}
        {task.description && (
          <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3 text-sm text-[#334155]">
            {task.description}
          </div>
        )}

        {/* Progress Updates */}
        {task.updates.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-3">
              Progress History
            </h4>
            <div className="space-y-2">
              {[...task.updates].reverse().map((u, i) => (
                <div key={i} className="flex gap-3">
                  <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#2447d9]" />
                  <div>
                    <p className="text-sm text-[#0f172a]">{u.note}</p>
                    <p className="text-xs text-[#94a3b8]">
                      {u.updatedBy} · {formatDate(u.updatedAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Note */}
        {task.status !== "DONE" && (
          <div>
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-2">
              Add Progress Note
            </h4>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Describe your progress…"
              rows={3}
              className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#2447d9] resize-none"
            />
            <Button
              size="sm"
              variant="secondary"
              className="mt-2"
              disabled={!note.trim() || addUpdate.isPending}
              onClick={handleAddNote}
            >
              <MessageSquarePlus className="h-3.5 w-3.5" />
              {addUpdate.isPending ? "Saving…" : "Add Note"}
            </Button>
          </div>
        )}

        {/* Status actions */}
        {nextStatuses.length > 0 && (
          <div className="flex items-center gap-2 border-t border-[#f1f5f9] pt-4">
            <span className="text-sm text-[#64748b]">Move to:</span>
            {nextStatuses.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={s === "DONE" ? "primary" : s === "BLOCKED" ? "ghost" : "secondary"}
                disabled={updateStatus.isPending}
                onClick={() => handleStatusChange(s)}
              >
                {s.replace("_", " ")}
              </Button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const STATUS_FILTERS: Array<{ label: string; value: TaskStatus | "ALL" }> = [
  { label: "All", value: "ALL" },
  { label: "To Do", value: "TODO" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Blocked", value: "BLOCKED" },
  { label: "Done", value: "DONE" },
];

export default function TasksPage() {
  const [filterStatus, setFilterStatus] = useState<TaskStatus | "ALL">("ALL");
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const statusParam = filterStatus === "ALL" ? undefined : filterStatus;
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMyTasks(statusParam);

  const tasks = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0f172a]">My Tasks</h1>
          <p className="mt-1 text-sm text-[#64748b]">View and manage tasks assigned to you.</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-[#e2e8f0] bg-white p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilterStatus(f.value)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                filterStatus === f.value
                  ? "bg-[#2447d9] text-white"
                  : "text-[#64748b] hover:text-[#0f172a]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <Card>
          <p className="text-center text-sm text-[#dc2626]">Failed to load tasks. Please refresh.</p>
        </Card>
      )}

      {!isLoading && !error && tasks.length === 0 && (
        <EmptyState
          icon={<ClipboardList className="h-8 w-8 text-[#94a3b8]" />}
          title="No tasks found"
          description={
            filterStatus === "ALL"
              ? "You have no tasks assigned yet."
              : `No tasks with status "${filterStatus.replace("_", " ")}".`
          }
        />
      )}

      {!isLoading && !error && tasks.length > 0 && (
        <div className="space-y-3">
          {tasks.map((task) => (
            <div
              key={task.taskId}
              className="group flex items-start gap-4 rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 hover:border-[#2447d9]/30 hover:shadow-sm transition-all cursor-pointer"
              onClick={() => setSelectedTask(task)}
            >
              <div className="mt-0.5 shrink-0">{STATUS_ICON[task.status]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-medium text-[#0f172a] group-hover:text-[#2447d9] transition-colors truncate">
                    {task.title}
                  </p>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PRIORITY_COLOR[task.priority]}`}
                    >
                      {task.priority}
                    </span>
                    <Badge variant={statusVariant(task.status)} className="text-xs">
                      {task.status.replace("_", " ")}
                    </Badge>
                  </div>
                </div>
                {task.description && (
                  <p className="mt-1 text-sm text-[#64748b] line-clamp-1">{task.description}</p>
                )}
                <div className="mt-2 flex items-center gap-3 text-xs text-[#94a3b8]">
                  {task.dueDate && <span>Due {formatDate(task.dueDate)}</span>}
                  {task.updates.length > 0 && (
                    <span className="flex items-center gap-1">
                      <MessageSquarePlus className="h-3 w-3" />
                      {task.updates.length} update{task.updates.length !== 1 ? "s" : ""}
                    </span>
                  )}
                  <span>By {task.createdBy}</span>
                </div>
              </div>
              <ChevronDown className="h-4 w-4 text-[#94a3b8] rotate-[-90deg] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] disabled:opacity-50 transition-colors"
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}

      {selectedTask && (
        <Modal
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          title={selectedTask.title}
          size="md"
        >
          <TaskDetailModal
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
            onRefresh={() => refetch()}
          />
        </Modal>
      )}
    </div>
  );
}
